import { Editor } from "@tiptap/react";
import { nanoid } from "nanoid";
import { useCallback, useEffect } from "react";
import { useRecoilValue } from "recoil";
import tippy, { Instance } from "tippy.js";
import EmailSelectorFamily, { EmailMode } from "../atoms/EmailAtom";
import { isURL, selectionMarks } from "../components/NewEmail/TiptapAutoLink";
import {
  getAnchorElement,
  getSelectedRange,
  onCursorInsideAnchor,
  selectAnchorElementURL,
} from "../utils/LinkUtils";
import { TippyPureHTML } from "../utils/TippyPureHTML";
import useLinkPopover from "./useLinkPopover";

function useLinkTooltips(
  editor: Editor,
  tooltips: React.MutableRefObject<{ [href: string]: Instance }>,
  tiptapMenu: Instance,
  isLoading: boolean,
  linkColor: string,
  mode: EmailMode,
  index?: number
) {
  const { openLinkModal } = useLinkPopover(editor, tooltips, mode, index);
  const email = useRecoilValue(EmailSelectorFamily({ mode, index }));

  const updateTooltips = useCallback(() => {
    if (!editor) return null;
    if (!process.browser) return;

    // Remove duplicated "data-tooltip-id" possibly generated due to pasting from BM editor.
    const allMaybeRepeatedLinks = Array.from(
      document.querySelectorAll<HTMLAnchorElement>(`.ProseMirror a[href]`)
    ) as HTMLAnchorElement[];

    allMaybeRepeatedLinks.forEach((l) => {
      const tooltipId = l.getAttribute("data-tooltip-id");

      if (
        tooltipId &&
        Array.from(
          document.querySelectorAll(`a[data-tooltip-id="${tooltipId}"]`)
        ).length > 1
      ) {
        l.setAttribute("data-tooltip-id", nanoid());
      }
    });

    //@ts-ignore
    window.berserkerTippyClickUnlink = (
      tooltipId: string,
      sanitizedUrl: string,
      text: string
    ) => {
      const isRaw = isURL(text);

      selectAnchorElementURL(tooltipId, tooltips.current);
      setTimeout(() => {
        const marks = selectionMarks(editor.state, editor.state.schema);
        editor.commands.removeURL();
        editor.commands.focus();
        editor.commands.insertContent({
          type: "text",
          text: isRaw ? `h‌${sanitizedUrl.substring(1)}` : text,
          marks: marks
            .filter((mark) => mark !== "underline")
            .map((mark) => ({
              type: mark,
            })),
        });
      }, 100);
    };

    //@ts-ignore
    window.berserkerTippySelect = (tooltipId: string) => {
      selectAnchorElementURL(tooltipId, tooltips.current);
    };

    let allLinks = Array.from(
      document.querySelectorAll<HTMLAnchorElement>(".ProseMirror a[href]")
    ) as HTMLAnchorElement[];

    let tooltipInstancesAux = {};

    allLinks.forEach((l) => {
      tooltipInstancesAux[l.getAttribute("data-tooltip-id")] = tippy(l, {
        content: (l) => TippyPureHTML.renderTippyHTML(l as HTMLAnchorElement),
        allowHTML: true,
        placement: "bottom",
        appendTo: document.body,
        popperOptions: {
          strategy: "fixed",
        },
        interactive: true,
        theme: "berserker",
        trigger: "mouseenter click",
      });

      l.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        (window as any).berserkerTippySelect(l.getAttribute("data-tooltip-id"));
        if (tiptapMenu) tiptapMenu.show();
      });
    });

    Object.values(tooltips.current).forEach((ti) => ti.destroy());
    tooltips.current = tooltipInstancesAux;

    //@ts-ignore
    window.berserkerTippyClickUpdate = (
      tooltipId: string,
      sanitizedUrl: string,
      text: string
    ) => {
      const anchorElement = getAnchorElement(tooltipId);
      if (!anchorElement) return;
      selectAnchorElementURL(tooltipId, tooltips.current);
      const range = getSelectedRange();
      if (!range) return;
      const linkURL = anchorElement.getAttribute("data-id");
      const linkLabel = anchorElement.getAttribute("data-label");

      openLinkModal(!!anchorElement, "", range, tooltipId, linkURL, linkLabel);
    };

    const allUnderlinedLinks = Array.from(
      document.querySelectorAll<HTMLAnchorElement>(`.ProseMirror u > a[href]`)
    ) as HTMLAnchorElement[];

    allUnderlinedLinks.forEach((link) => {
      const { parentNode } = link;
      if (
        parentNode.nodeName === "U" &&
        parentNode.childNodes.length - 1 === 0
      ) {
        // @ts-ignore
        if (!parentNode.getEventListeners()?.DOMNodeInserted)
          parentNode.addEventListener("DOMNodeInserted", onCursorInsideAnchor);
        // // @ts-ignore
        // if (!parentNode.getEventListeners()?.DOMNodeRemoved)
        //   parentNode.addEventListener("DOMNodeRemoved", onLinkRemoval);
      }
    });
  }, [editor, isLoading, email.body, tiptapMenu, linkColor]);

  useEffect(() => {
    updateTooltips();
  }, [updateTooltips]);

  function mousedownHandlerEditor(event: MouseEvent) {
    // Exception to clicks inside the tooltips themselves.
    if (
      event.target &&
      (event.target as any).className?.includes?.("tooltipAction")
    ) {
      return;
    }

    Object.values(tooltips.current).forEach((tippyInstance) =>
      tippyInstance.disable()
    );
  }

  function mouseupHandlerEditor(event: MouseEvent) {
    Object.values(tooltips.current).forEach((tippyInstance) =>
      tippyInstance.enable()
    );
  }

  useEffect(() => {
    if (!editor) return null;
    // Controls wether link tooltips should be displayed or not, especially
    // when selecting content (should not be displayed in this case).

    document.body.addEventListener("mousedown", mousedownHandlerEditor);
    document.body.addEventListener("mouseup", mouseupHandlerEditor);

    return () => {
      document.body.removeEventListener("mousedown", mousedownHandlerEditor);
      document.body.removeEventListener("mouseup", mouseupHandlerEditor);
    };
  }, [
    editor,
    mousedownHandlerEditor,
    mouseupHandlerEditor,
    tooltips.current.length,
  ]);
}

export default useLinkTooltips;
